import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useLocation, useNavigate,Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { buildReferrer, getSearchParam } from "Utils/url";
import refererApi from "Apis/refererApi";
import TcktRegApi from "Apis/TcktRegApi";
import TcktEvntApi from "Apis/TcktEvntApi";
import "Assets/css/Connect.css"

interface TcktMission {
    mssnNo: number;
    mssnNm: string;
    evntCd: string;
    mssnDesc: string;
    mssnYn: "Y" | "N";
    allYn: "Y" | "N";
}

interface ResultModalPayload {
    h1?: string;
    p: string;
    btnLabel?: string;
    action?: () => void;
}

interface ResultModalContent {
    h1: string;
    p: string;
    btnLabel: string;
}

type ResultModalCode =
    | "LOGIN_REQUIRED"
    | "MEMB0000"
    | "MEMB3208"
    | "MEMB3212"
    | "MEMB3210"
    | "ALL_MISSION_COMPLETE"
    | "DEFAULT";

const EVENT_END_AT_UTC_MS = Date.UTC(2026, 4, 13, 15, 0, 0);

const CafeMissionUi = () => {
   const { langData }: any = useOutletContext();
   const location = useLocation();
    const navigate = useNavigate();
    const isEventEnded = Date.now() >= EVENT_END_AT_UTC_MS;
    const loginInfoRaw = useSelector((state: any) => state.LoginInfoState?.LoginInfo);
    const loginInfoState = loginInfoRaw?.apiBody;
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultModalContent, setResultModalContent] = useState<ResultModalContent>({
        h1: "알림",
        p: "",
        btnLabel: "내 응모권 현황 확인하기",
    });
    const [resultModalCode, setResultModalCode] = useState<ResultModalCode | null>(null);
    const [modalAction, setModalAction] = useState<(() => void) | null>(null);
    const [evntLstState, setEvntLstState] = useState<TcktMission[]>([]);
    const tcktRegRequestedKeysRef = useRef<Set<string>>(new Set());
    const completeSoundRef = useRef<HTMLAudioElement | null>(null);

   const getCurrentParams = () => ({
      currentGameSvcCd: getSearchParam(location.search, "gameSvcCd"),
      currentCtntCtgrCd: getSearchParam(location.search, "ctntKindCd"),
   });

   const currentParams = useMemo(() => getCurrentParams(), [location.search]);
   const loginReferrer = useMemo(() => buildReferrer("/Connect/ConnectMission", location.search), [location.search]);

    const playCompleteSound = () => {
        const audio = completeSoundRef.current;
        if (!audio) {
            return;
        }

        audio.currentTime = 0;
        audio.play().catch(() => {});
    };

    const resultModalPayloadMap = useMemo<Record<ResultModalCode, ResultModalPayload>>(() => ({
        LOGIN_REQUIRED: {
            h1: "로그인 안내",
            p: "모든 미션을 완료하고 베어브릭1000%의 <br />주인공이 되어보세요!",
            btnLabel: "로그인/회원가입 바로가기",
            action: () => {
                navigate(`/Login?referrer=${loginReferrer}`);
            },
        },
        MEMB0000: {
            h1: "이벤트 안내",
            p: langData?.EVNT_TCKT_REG_SUCCESS_00 || "모든 미션을 완료하고 베어브릭1000%의 <br />주인공이 되어보세요!",
        },
        MEMB3208: {
            h1: "NFC 인식 실패",
            p: "현장의 NFC를 다시 태그해주세요.",
        },
        MEMB3212: {
            h1: "NFC 인식 실패",
            p: "Connect에 방문하여 미션에 도전해주세요!",
        },
        MEMB3210: {
            h1: "이미 완료된 미션입니다!",
            p: "현장에 숨겨진 다른 미션을 찾아보세요!",
        },
        ALL_MISSION_COMPLETE: {
            h1: "모든 미션 완료!",
            p: "지금 바로 베어브릭 1000%의 주인공이 되어보세요!",
        },
        DEFAULT: {
            h1: isEventEnded ? "기간 이후 종료된 이벤트입니다." : "잘못된 접근입니다",
            p: isEventEnded
                ? "당첨자 안내는 [ 공식 홈페이지 > NEWS > \"이벤트\" ] 페이지에서 별도 공지됩니다."
                : "현장의 NFC를 다시 태그해주세요.",
        },
    }), [isEventEnded, langData, loginReferrer, navigate]);

    const openResultModal = ({ h1, p, btnLabel, action }: ResultModalPayload) => {
        setResultModalContent({
          h1: h1 || "알림",
          p,
          btnLabel: btnLabel ||"내 응모권 현황 확인하기",
        });
        setModalAction(() => action || (() => navigate(`/Connect/ConnectEvent${location.search}`)));
        setResultModalOpen(true);
    };

    const openResultModalByCode = (code: ResultModalCode, overrides?: Partial<ResultModalPayload>) => {
        const basePayload = resultModalPayloadMap[code] || resultModalPayloadMap.DEFAULT;
        setResultModalCode(code);
        openResultModal({
            ...basePayload,
            ...overrides,
            p: overrides?.p ?? basePayload.p,
        });
    };

   const closeResultModal = () => {
      setResultModalOpen(false);
            setResultModalCode(null);
      const action = modalAction;
      setModalAction(null);
      if (action) action();
   };

    const dismissResultModal = () => {
        setResultModalOpen(false);
                setResultModalCode(null);
        setModalAction(null);
    };

    useEffect(() => {
        if (!resultModalOpen) {
            return;
        }

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [resultModalOpen]);

    useEffect(() => {
        refererApi();
    }, []);

    useEffect(() => {
        if (loginInfoRaw === null) {
            return;
        }

        if (!loginInfoState?.login) {
            openResultModalByCode("LOGIN_REQUIRED");
        } else {
            const tcktRegRequestKey = `${location.pathname}${location.search}|LOGIN_Y`;
            if (!tcktRegRequestedKeysRef.current.has(tcktRegRequestKey)) {
                tcktRegRequestedKeysRef.current.add(tcktRegRequestKey);
                TcktRegApi().then(resp => {
                    switch (resp?.apiHdr?.code) {
                        case "MEMB0000":
                            playCompleteSound();
                            if(resp?.apiBody?.allYn ==="Y") {
                                openResultModalByCode("ALL_MISSION_COMPLETE");
                            }else {
                                openResultModalByCode("MEMB0000", {
                                    h1: resp?.apiBody?.evntMsg,
                                });
                            }
                        break;
                        case "MEMB3208" :
                            openResultModalByCode("MEMB3208");
                        break;
                        case "MEMB3212" :
                                openResultModalByCode("MEMB3212");
                        break;
                        case "MEMB3210":
                            openResultModalByCode("MEMB3210");
                        break;
                        default:
                            openResultModalByCode("DEFAULT");
                    }
                });
            }
        }

        TcktEvntApi().then(resp => {
            switch (resp?.apiHdr?.code) {
                case "MEMB0000":
                    setEvntLstState(Array.isArray(resp?.apiBody) ? resp.apiBody : []);
                break;
                default:
                    setEvntLstState([]);
            }
        }).catch(err => {
            console.error('TcktEvntApi error', err);
            setEvntLstState([]);
        });
    }, [loginInfoRaw, loginInfoState?.login, langData, loginReferrer, navigate]);

    return (
        <>
            <div className="Container700">
                <div className="ConnectMissionTitl">
                </div>
                <div className="NFCMissionUlWrap">
                    <div className="NFCMissionUl">
                        <img src={process.env.PUBLIC_URL + "/images/Connect/MissionLstTitl.png"} alt="NFC 태그 이벤트" />
                        <ul>
                           {evntLstState.length > 0 ? evntLstState.map((evnt) => (
                                <li key={evnt.evntCd || String(evnt.mssnNo)} className={"missionLi" + (evnt.mssnYn === "Y" ? " missionComplete" : "")}>
                                    <div className="MissionNm">{evnt.mssnNm}</div>
                                    <div className="MissionDesc">{evnt.mssnDesc}</div>
                                    <div className="complete">
                                        <img src={process.env.PUBLIC_URL + (evnt.mssnYn === "Y" ? "/images/Connect/Complete.png" : "")} alt="미션 상태 아이콘" />        
                                    </div>
                                </li>
                           )):<></>}   
                        </ul>
                    </div>
                    <Link className="MissionLstBtn" to={'/Connect/ConnectEvent' + location.search}>
                           <img src={process.env.PUBLIC_URL + "/images/Connect/MissionLstBtn.png"} alt="미션 이동" />
                    </Link>
                </div>
                <div className="underLineFx">
                    <img className="underLine" src={process.env.PUBLIC_URL + "/images/Connect/MissionUnderLine.png"} alt="미션 구분선" />
                    <img className="underLineMo" src={process.env.PUBLIC_URL + "/images/Connect/MissionUnderLineMo.png"} alt="미션 구분선" />
                </div>
            </div>

            {resultModalOpen && (
                <div className="mdalCommWrap connectMissionModalWrap">
                    <div className="mdalBg" onClick={dismissResultModal}></div>
                    <div className="mdalCommContainer  connectMissionModalContainer">
                        <div className="mdalCommCloseBtn" onClick={dismissResultModal}>
                            <i className="close"></i>
                        </div>
                        <div className={`mdalCtxtInfo connectMissionModalMessage${resultModalCode === "MEMB0000" || resultModalCode === "ALL_MISSION_COMPLETE" ? " success" : ""}`}>
                            <h1>{resultModalContent.h1}</h1>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: (resultModalContent.p || "").replace(/\n/g, "<br />"),
                                }}
                            ></p>
                        </div>
                        <button className="mdalCommAgreeBtn connectMissionModalButton" onClick={closeResultModal}>
                            <p>{resultModalContent.btnLabel}</p>
                        </button>
                    </div>
                </div>
            )}
            <audio ref={completeSoundRef} preload="auto" style={{ display: "none" }}>
                <source src={process.env.PUBLIC_URL + "/audio/ui_complete.wav"} type="audio/wav" />
            </audio>
        </>
    );
};



export default CafeMissionUi;